// NEW_FILE_JUSTIFIED: Settings page for the AI auto-reply feature at /settings/ai-replies
import { Skeleton } from '@/components/ui/skeleton';
import { useAiReplySettings } from '@/hooks/useAiReplySettings';
import { useStyleAnalysis } from '@/hooks/useStyleAnalysis';
import { useLearnedRules } from '@/hooks/useLearnedRules';
import { AiReplyGlobalSettings } from '@/components/aiReply/AiReplyGlobalSettings';
import { AiReplyFollowUpSettings } from '@/components/aiReply/AiReplyFollowUpSettings';
import { AiReplyContactOverrides } from '@/components/aiReply/AiReplyContactOverrides';
import { StyleAnalysisConfigCard } from '@/components/aiReply/StyleAnalysisConfigCard';
import { StyleAnalysisResultsCard } from '@/components/aiReply/StyleAnalysisResultsCard';
import { DEFAULT_STYLE_ANALYSIS_SETTINGS } from '@/@types/aiReply';

export function AiRepliesSettingsPage() {
  const {
    settings,
    overrides,
    timezone,
    updateSettings,
    updateTimezone,
    toggleOverride,
    deleteOverride,
    isLoading,
  } = useAiReplySettings();

  const { profiles, triggerAnalysis, updateProfile } = useStyleAnalysis();
  const { rules, addRule, updateRule, toggleRule, deleteRule } = useLearnedRules();

  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  const lookbackDays =
    settings?.analysisLookbackDays ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analysisLookbackDays;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">AI Replies</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure AI-powered auto-reply and follow-up behaviour across all platforms
          </p>
        </div>

        <AiReplyGlobalSettings
          settings={settings}
          timezone={timezone}
          onUpdate={updateSettings}
          onTimezoneChange={updateTimezone}
        />

        <AiReplyFollowUpSettings settings={settings} onUpdate={updateSettings} />

        <StyleAnalysisConfigCard settings={settings} onUpdate={updateSettings} />

        <StyleAnalysisResultsCard
          profiles={profiles}
          analyzeEnabled={{
            gmail: settings?.analyzeGmail ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analyzeGmail,
            whatsapp: settings?.analyzeWhatsapp ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analyzeWhatsapp,
            telegram: settings?.analyzeTelegram ?? DEFAULT_STYLE_ANALYSIS_SETTINGS.analyzeTelegram,
          }}
          onTriggerAnalysis={triggerAnalysis}
          onUpdateProfile={updateProfile}
          lookbackDays={lookbackDays}
          learnedRules={rules}
          onAddRule={addRule}
          onUpdateRule={updateRule}
          onToggleRule={toggleRule}
          onDeleteRule={deleteRule}
        />

        <AiReplyContactOverrides
          overrides={overrides}
          onToggle={toggleOverride}
          onDelete={deleteOverride}
        />
      </div>
    </div>
  );
}
